import Debug from '../utils/Debug.js';

/**
 * 3D Vector class for mathematical operations
 * Provides common vector operations for 3D space
 */
export default class Vector3 {
  /**
   * Create a new Vector3
   * @param {number} x - X coordinate
   * @param {number} y - Y coordinate
   * @param {number} z - Z coordinate
   */
  constructor(x = 0, y = 0, z = 0) {
    this.x = x;
    this.y = y;
    this.z = z;
  }

  /**
   * Add another vector to this vector
   * @param {Vector3} v - Vector to add
   * @return {Vector3} New vector with result
   */
  add(v) {
    return new Vector3(this.x + v.x, this.y + v.y, this.z + v.z);
  }

  /**
   * Subtract another vector from this vector
   * @param {Vector3} v - Vector to subtract
   * @return {Vector3} New vector with result
   */
  subtract(v) {
    return new Vector3(this.x - v.x, this.y - v.y, this.z - v.z);
  }

  /**
   * Multiply this vector by a scalar
   * @param {number} scalar - Scalar value
   * @return {Vector3} New vector with result
   */
  multiply(scalar) {
    return new Vector3(this.x * scalar, this.y * scalar, this.z * scalar);
  }

  /**
   * Divide this vector by a scalar
   * @param {number} scalar - Scalar value
   * @return {Vector3} New vector with result
   */
  divide(scalar) {
    if (scalar === 0) {
      Debug.warn('Vector3', 'Division by zero');
      return new Vector3(this.x, this.y, this.z);
    }
    return new Vector3(this.x / scalar, this.y / scalar, this.z / scalar);
  }

  /**
   * Calculate dot product with another vector
   * @param {Vector3} v - Vector to dot with
   * @return {number} Dot product result
   */
  dot(v) {
    return this.x * v.x + this.y * v.y + this.z * v.z;
  }

  /**
   * Calculate cross product with another vector
   * @param {Vector3} v - Vector to cross with
   * @return {Vector3} New vector with cross product result
   */
  cross(v) {
    return new Vector3(
      this.y * v.z - this.z * v.y,
      this.z * v.x - this.x * v.z,
      this.x * v.y - this.y * v.x
    );
  }

  /**
   * Calculate the length (magnitude) of this vector
   * @return {number} Length of vector
   */
  length() {
    return Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z);
  }

  /**
   * Calculate squared length (faster than length())
   * @return {number} Squared length
   */
  lengthSquared() {
    return this.x * this.x + this.y * this.y + this.z * this.z;
  }

  /**
   * Normalize this vector (make length = 1)
   * @return {Vector3} New normalized vector
   */
  normalize() {
    const len = this.length();
    if (len === 0) {
      Debug.warn('Vector3', 'Cannot normalize zero vector');
      return new Vector3(0, 0, 0);
    }
    return this.divide(len);
  }

  /**
   * Calculate distance to another vector
   * @param {Vector3} v - Target vector
   * @return {number} Distance
   */
  distanceTo(v) {
    return this.subtract(v).length();
  }

  /**
   * Calculate squared distance to another vector (faster)
   * @param {Vector3} v - Target vector
   * @return {number} Squared distance
   */
  distanceToSquared(v) {
    return this.subtract(v).lengthSquared();
  }

  /**
   * Clone this vector
   * @return {Vector3} New vector with same values
   */
  clone() {
    return new Vector3(this.x, this.y, this.z);
  }

  /**
   * Set the values of this vector
   * @param {number} x - X coordinate
   * @param {number} y - Y coordinate
   * @param {number} z - Z coordinate
   * @return {Vector3} This vector
   */
  set(x, y, z) {
    this.x = x;
    this.y = y;
    this.z = z;
    return this;
  }

  /**
   * Create a Vector3 from an object with x, y, z properties
   * @param {Object} obj - Object with x, y, z properties
   * @return {Vector3} New Vector3
   */
  static fromObject(obj) {
    return new Vector3(obj.x || 0, obj.y || 0, obj.z || 0);
  }

  /**
   * Convert to plain object
   * @return {Object} Object with x, y, z properties
   */
  toObject() {
    return { x: this.x, y: this.y, z: this.z };
  }

  /**
   * Check if this vector equals another vector
   * @param {Vector3} v - Vector to compare
   * @return {boolean} True if equal
   */
  equals(v) {
    return this.x === v.x && this.y === v.y && this.z === v.z;
  }

  /**
   * Convert to string representation
   * @return {string} String representation
   */
  toString() {
    return `Vector3(${this.x}, ${this.y}, ${this.z})`;
  }

  /**
   * Create a zero vector
   * @return {Vector3} Zero vector
   */
  static zero() {
    return new Vector3(0, 0, 0);
  }

  /**
   * Create a unit vector along X axis
   * @return {Vector3} Unit X vector
   */
  static unitX() {
    return new Vector3(1, 0, 0);
  }

  /**
   * Create a unit vector along Y axis
   * @return {Vector3} Unit Y vector
   */
  static unitY() {
    return new Vector3(0, 1, 0);
  }

  /**
   * Create a unit vector along Z axis
   * @return {Vector3} Unit Z vector
   */
  static unitZ() {
    return new Vector3(0, 0, 1);
  }
}
